import { useFireOnce } from 'dotcom-core/src/hooks/useFireOnce';
import { useEffect } from 'react';
import type { Cart, ProductVariant } from '~/graphql';
import {
  type EcommerceEvent,
  pushEcommerceEvent,
  pushProductEcommerceEvent,
} from '~/helpers/tracking';
import type { ProductInterface } from '~/routes/$language.pricing-v2.$slug/route';

type EcommerceTrackingProps = {
  event: EcommerceEvent;
  customerType: string | null | undefined;
  product?: ProductInterface;
  variant?: ProductVariant | null | undefined;
  cart?: Cart;
};

export function useEcommerceTracking({
  event,
  customerType,
  product,
  variant,
  cart,
}: EcommerceTrackingProps) {
  const fireEventOnce = useFireOnce();

  useEffect(() => {
    fireEventOnce(() => {
      if (event === 'view_item' && product) {
        pushProductEcommerceEvent({
          customerType,
          product,
          variant,
          event,
        });
      }

      if (cart?.items?.length) {
        pushEcommerceEvent(event, {
          cart_id: cart?.id,
          currency: cart?.prices?.grandTotal?.currency,
          shipping_tier:
            cart?.shippingAddresses?.[0]?.selectedShippingMethod?.methodCode ||
            '',
          shipping:
            cart?.shippingAddresses?.[0]?.selectedShippingMethod?.amountInclTax
              .value,
          coupons: Array.isArray(cart?.appliedCoupons)
            ? cart.appliedCoupons.map((coupon) => coupon?.code || undefined)
            : undefined,
          value: cart?.prices?.grandTotal?.value,
          items: cart?.items?.map((item) => ({
            // item_id/item_name should come from the parent product,
            // but it's not available in the cart item data yet.
            // @TODO [ECOM-85, ECOM-84]
            item_id: item?.product?.sku ?? '',
            item_name: item?.product?.name ?? '',
            sku_id: item?.product?.sku,
            sku_name: item?.product?.name,
            sdbs_id:
              item?.product && 'sdbsId' in item.product
                ? item.product.sdbsId
                : '',
            price: item?.prices?.price.value || 0,
            quantity: item?.quantity,
          })),
        });
      }
    });
  }, [event, variant, product, customerType, fireEventOnce, cart]);
}
